import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import { useRef } from 'react';
import { FieldArray } from 'formik';
import AutoComplete from '../../../components/AutoComplete';
import { Flavor } from '../../../store/flavor/api/interface';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  findSelectShape,
  searchSelectShape,
} from '../../../store/selectShape/api';
import { setSkip as setSkipShape } from '../../../store/selectShape/selectShapeSlice';
import _, { uniqBy } from 'lodash';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';
import { getAssetURl } from '../../../utils/url';
import { isValidArray } from '../../../utils/valid';
import { Shape } from '../../../store/shape/api/interface';
import { getShapeMapping, resolveMappingForFile } from '../../../utils/shapeMapping';

interface IProps {
  isAddMode: boolean;
  response: Flavor | undefined;
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: any;
}

const DetailsForm: React.FC<IProps> = ({
  isAddMode,
  response,
  values,
  // handleChange,
  setFieldValue,
  errors,
  touched,
}) => {
  const arrayHelpersRef = useRef<any>(null);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  // Shape Data
  const {
    data: shapeData,
    isLoading: isShapeLoading,
    skip: shapeSkip,
    hasMore: shapeHasMore,
    status: shapeStatus,
  } = useAppSelector((state) => state.selectShape);

  useEffect(() => {
    if (shapeStatus === 'idle') {
      dispatch(
        findSelectShape({
          activeOnly: true,
          limit: 50,
          skip: shapeSkip,
        })
      );
    }
  }, [shapeSkip, shapeStatus]);

  const onScrollFetchShape = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      shapeHasMore
    ) {
      dispatch(setSkipShape({ skip: shapeSkip + 10 }));
      dispatch(
        findSelectShape({
          activeOnly: true,
          limit: 50,
          skip: shapeSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchShape = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const params = {
      activeOnly: true,
      ...(!e.target.value && { limit: 50 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipShape({ skip: 0 }));
    dispatch(searchSelectShape(params));
  };

  const shapeDetails =
    response?.shapeImages?.map((item) => {
      return item?.shape;
    }) ?? [];

  const uniqueShapeData = uniqBy(
    [
      ...(!isAddMode && response?.shapeImages ? [...shapeDetails] : []),
      ...shapeData,
    ],
    'id'
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  useEffect(() => {
    if (isValidArray(shapeData) && isAddMode) {
      shapeData?.map((element: Shape, index: number) => {
        if (element?.id) {
          setFieldValue(`shapeItems[${index}].shapeId`, element?.id);
          setFieldValue(`shapeItems[${index}].top`, '');
          setFieldValue(`shapeItems[${index}].front`, '');
        }
      });
    }
  }, [shapeData, isAddMode]);

  useEffect(() => {
    if (isValidArray(shapeData) && !isAddMode) {
      const valueShapeIds = _.map(values?.shapeItems, 'shapeId');
      const mainShapeIds = _.map(shapeData, 'id');
      const missingShapeIds = _.difference(mainShapeIds, valueShapeIds);
      if (isValidArray(missingShapeIds)) {
        const newItems = missingShapeIds?.map((id) => ({
          shapeId: id,
          top: '',
          front: '',
        }));
        const updatedValueArray = _.concat(values?.shapeItems, newItems);
        if (updatedValueArray.length !== values?.shapeItems?.length) {
          setFieldValue(`shapeItems`, updatedValueArray);
        }
      }
    }
  }, [shapeData, isAddMode, values?.shapeItems]);

  const handleRemoveClick = (idx: number) => {
    setSelectedIndex(idx);
    setOpenConfirmDialog(true);
  };

  const handleConfirmRemove = () => {
    if (selectedIndex !== null) {
      // Only clear the image fields, keep the shape item
      setFieldValue(`shapeItems[${selectedIndex}].top`, '');
      setFieldValue(`shapeItems[${selectedIndex}].front`, '');
    }
    setOpenConfirmDialog(false);
    setSelectedIndex(null);
  };

  const handleCancelRemove = () => {
    setOpenConfirmDialog(false);
    setSelectedIndex(null);
  };

  const handleBulkUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      const mapping = getShapeMapping();

      // 1. CLEAR ALL EXISTING IMAGES (RESET)
      if (values?.shapeItems) {
        const resetItems = values.shapeItems.map((item: any) => ({
          ...item,
          top: '',
          front: '',
        }));
        await setFieldValue('shapeItems', resetItems);
      }

      // 2. PROCESS FILES
      let successCount = 0;
      const uploadPromises = files.map(async (file) => {
        const match = resolveMappingForFile(file.name, mapping);

        if (match) {
          let targetIndex = -1;

          if (values?.shapeItems) {
            targetIndex = values.shapeItems.findIndex((item: any) => {
              // Match by ID
              if (match.shapeId && item.shapeId === match.shapeId) return true;

              // Match by Code
              if (match.shapeCode) {
                const shape = uniqueShapeData.find((s) => s.id === item.shapeId);
                if (shape && shape.code === match.shapeCode) return true;
              }
              return false;
            });
          }

          if (targetIndex !== -1) {
            try {
              const uploadType =
                match.type === 'top'
                  ? DocumentType.ToppingShapeTop
                  : DocumentType.ToppingShapeFront;
              const result: any = await uploadAssets(
                file,
                uploadType,
                accessToken
              );

              if (result?.data?.keypath) {
                successCount++;
                return {
                  index: targetIndex,
                  type: match.type,
                  path: result.data.keypath,
                };
              }
            } catch (err) {
              console.error('Bulk upload error for file', file.name, err);
            }
          }
        }
        return null;
      });

      const results = await Promise.all(uploadPromises);

      // 3. APPLY UPDATES
      results.forEach((res) => {
        if (res) {
          setFieldValue(`shapeItems[${res.index}].${res.type}`, res.path);
        }
      });

      if (successCount > 0) {
        toast.success(`Images added successfully: ${successCount}`);
      } else {
        toast.error('No images matched the shape items.');
      }

      if (bulkInputRef.current) {
        bulkInputRef.current.value = '';
      }
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Stack
        direction='column'
        alignItems='flex-start'
        sx={{ mb: 2 }}
        spacing={2}
      >
        <Typography
          variant='h6'
          sx={{ color: 'text.disabled' }}
        >
          Shape Items:
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant='contained'
            component='label'
            startIcon={<Iconify icon='eva:cloud-upload-fill' />}
          >
            Bulk Upload Images
            <input
              ref={bulkInputRef}
              type='file'
              hidden
              multiple
              accept='image/*'
              onChange={handleBulkUpload}
            />
          </Button>
          <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold' }}>
            Note: Existing images will be removed while using multi-image upload
          </Typography>
        </Stack>
      </Stack>
      <Stack
        divider={
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed' }}
          />
        }
        spacing={3}
      >
        <FieldArray
          name='shapeItems'
          render={(arrayHelpers: any) => {
            arrayHelpersRef.current = arrayHelpers;
            return (
              <>
                {values?.shapeItems?.map((item: any, idx: any) => {
                  return (
                    <React.Fragment key={item?.shapeId + idx}>
                      <Stack
                        alignItems='flex-end'
                        spacing={1.5}
                      >
                        <Grid
                          container
                          spacing={3}
                        >
                          <Grid
                            item
                            xs={12}
                            sm={4}
                          >
                            <AutoComplete
                              onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                } else if (newValue) {
                                  setFieldValue(
                                    `shapeItems[${idx}].shapeId`,
                                    newValue.id
                                  );
                                }
                              }}
                              onInputChange={(event, newInputValue) => {
                                if (!newInputValue) {
                                  setFieldValue(
                                    `shapeItems[${idx}].shapeId`,
                                    ''
                                  );
                                }
                              }}
                              renderOption={(props, option) => (
                                <li {...props}>{option.labelEn}</li>
                              )}
                              getOptionLabel={(option) => option.labelEn}
                              error={Boolean(
                                touched?.shapeItems?.[idx]?.shapeId &&
                                errors?.shapeItems?.[idx]?.shapeId
                              )}
                              helperText={
                                Boolean(
                                  touched?.shapeItems?.[idx]?.shapeId &&
                                  errors?.shapeItems?.[idx]?.shapeId
                                )
                                  ? errors.shapeItems?.[idx]?.shapeId
                                  : ''
                              }
                              fetchFunction={onScrollFetchShape}
                              label={'Select Shape'}
                              loading={isShapeLoading}
                              options={uniqueShapeData}
                              searchFunction={searchShape}
                              value={
                                uniqueShapeData?.filter(
                                  (shape) =>
                                    shape?.id ===
                                    values.shapeItems?.[idx]?.shapeId
                                )?.[0] || null
                              }
                              disabled={true}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={`${values?.shapeItems?.[idx]?.top}` ? 8 : 12}
                            sm={`${values?.shapeItems?.[idx]?.top}` ? 2.5 : 4}
                          >
                            <TextField
                              type='file'
                              fullWidth
                              name={`shapeItems[${idx}].top`}
                              id='top'
                              onChange={(e) =>
                                handleFileChange(
                                  e as React.ChangeEvent<HTMLInputElement>,
                                  DocumentType.ToppingShapeTop,
                                  `shapeItems[${idx}].top`
                                )
                              }
                              inputProps={{
                                multiple: false,
                                accept: 'image/*',
                              }}
                              InputLabelProps={{ shrink: true }}
                              variant='outlined'
                              label={'Top Image'}
                              error={Boolean(
                                touched?.shapeItems?.[idx]?.top &&
                                errors?.shapeItems?.[idx]?.top
                              )}
                              helperText={
                                Boolean(
                                  touched?.shapeItems?.[idx]?.top &&
                                  errors?.shapeItems?.[idx]?.top
                                ) && errors.shapeItems?.[idx]?.top
                              }
                            />
                          </Grid>

                          {values?.shapeItems?.[idx]?.top && (
                            <Grid
                              item
                              xs={4}
                              sm={1.5}
                            >
                              <Avatar
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  values?.shapeItems?.[idx]?.top,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  width: { xs: '75px' },
                                  height: 50,
                                }}
                              />
                            </Grid>
                          )}

                          <Grid
                            item
                            xs={`${values?.shapeItems?.[idx]?.front}` ? 8 : 12}
                            sm={`${values?.shapeItems?.[idx]?.front}` ? 2.5 : 4}
                          >
                            <TextField
                              type='file'
                              fullWidth
                              name={`shapeItems[${idx}].front`}
                              id='front'
                              onChange={(e) =>
                                handleFileChange(
                                  e as React.ChangeEvent<HTMLInputElement>,
                                  DocumentType.ToppingShapeFront,
                                  `shapeItems[${idx}].front`
                                )
                              }
                              inputProps={{
                                multiple: false,
                                accept: 'image/*',
                              }}
                              InputLabelProps={{ shrink: true }}
                              variant='outlined'
                              label={'Front Image'}
                              error={Boolean(
                                touched?.shapeItems?.[idx]?.front &&
                                errors?.shapeItems?.[idx]?.front
                              )}
                              helperText={
                                Boolean(
                                  touched?.shapeItems?.[idx]?.front &&
                                  errors?.shapeItems?.[idx]?.front
                                ) && errors.shapeItems?.[idx]?.front
                              }
                            />
                          </Grid>

                          {values?.shapeItems?.[idx]?.front && (
                            <Grid
                              item
                              xs={4}
                              sm={1.5}
                            >
                              <Avatar
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  values?.shapeItems?.[idx]?.front,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  width: { xs: '75px' },
                                  height: 50,
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                        {
                          <div>
                            {idx + 1 === values?.shapeItems?.length && (
                              <Button
                                size='small'
                                startIcon={<Iconify icon='eva:plus-fill' />}
                                disabled={true}
                                onClick={() =>
                                  arrayHelpersRef.current.push({
                                    shapeId: '',
                                    top: '',
                                    front: '',
                                  })
                                }
                              >
                                Add Shape Item
                              </Button>
                            )}
                            <Button
                              size='small'
                              color="error"
                              startIcon={<Iconify icon='eva:trash-2-outline' />}
                              onClick={() => handleRemoveClick(idx)}
                              sx={{ ml: 2 }}
                            >
                              Remove Images
                            </Button>
                          </div>
                        }
                      </Stack>
                    </React.Fragment>
                  );
                })}
              </>
            );
          }}
        ></FieldArray>
      </Stack>
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelRemove}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Remove Images"}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to remove the images for this shape item?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button onClick={handleConfirmRemove} color="error" autoFocus>
            Remove Images
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetailsForm;
